import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Package, Tag, Boxes, Ruler, Calendar,
  AlertTriangle, FileText, Edit3, Copy,
  RefreshCw, EyeOff, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import { Product } from '../../types';
import { productService } from '../../services/productService';
import api from '../../services/api';
import { aggiornamentiService } from '../../services/aggiornamentiService';
import { messaggioDiretto } from '../../components/admin/AggiornaDalGestionale';
import { getProductThumb } from '../../data/productImages';
import { PRODUCT_CATEGORIES } from '../../data/siteData';
import { ListiniBycodart } from '../../components/admin/ListiniBycodart';
import { BoxDiscountEditor } from '../../components/admin/BoxDiscountEditor';
import { PromoEditor } from '../../components/admin/PromoEditor';
import { AdminProductImages } from '../../components/admin/AdminProductImages';
import { AdminProductAttachments } from '../../components/admin/AdminProductAttachments';

// ── Field row helper ─────────────────────────────────────────────
function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-secondary/60 last:border-b-0">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${isEmpty ? 'text-muted-foreground italic' : 'text-foreground'}`}>
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
}

function Section({ icon: Icon, title, children }: {
  icon: React.ElementType; title: string; children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <Icon className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        {children}
      </div>
    </div>
  );
}

// ── Compatibilità (lista da abbinatore_associazioni) ───────────────
interface CompatModel {
  id: string;
  nome: string;
  marca: string;
  tipoMacchina: string;
  tipoMacchinaLabel: string;
  codiceOem: string;
}

function CompatibilitaCard({ sku }: { sku: string }) {
  const [modelli, setModelli] = useState<CompatModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<{ modelli: CompatModel[]; total: number }>(`/api/abbinatore/sku/${encodeURIComponent(sku)}/modelli`)
      .then(r => { if (!cancelled) setModelli(r.data.modelli || []); })
      .catch(() => { if (!cancelled) setModelli([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sku]);

  const filtered = filter.trim()
    ? modelli.filter(m => `${m.marca} ${m.nome}`.toLowerCase().includes(filter.trim().toLowerCase()))
    : modelli;

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <div className="flex items-center gap-2">
          <Printer className="w-4 h-4 text-[hsl(214,58%,41%)]" />
          <h2 className="text-sm font-semibold tracking-tight">Compatibilità macchine</h2>
          {!loading && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider bg-[hsl(214,58%,41%)]/10 text-[hsl(214,58%,41%)] px-1.5 py-0.5 rounded"
              data-testid="admin-compat-count"
            >
              {modelli.length}
            </span>
          )}
        </div>
        {modelli.length > 0 && (
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filtra marca o modello…"
            className="h-7 px-2 text-xs rounded border bg-white w-56 focus:outline-none focus:ring-1 focus:ring-[hsl(214,58%,41%)]/40"
            data-testid="admin-compat-filter"
          />
        )}
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-xs">Caricamento abbinamenti…</span>
          </div>
        ) : modelli.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="admin-compat-empty">
            Nessun modello macchina compatibile sincronizzato dal DB abbinamenti.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Nessun risultato per "{filter}".</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              {modelli.length} model{modelli.length === 1 ? 'lo' : 'li'} compatibil{modelli.length === 1 ? 'e' : 'i'} —
              fonte: <code className="font-mono">abbinatore_associazioni</code>.
            </p>
            <div className="overflow-x-auto -mx-4 sm:mx-0" data-testid="admin-compat-table">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-[hsl(var(--dtk-canvas))] text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 border-b w-12">#</th>
                    <th className="text-left font-medium px-3 py-2 border-b">Marca</th>
                    <th className="text-left font-medium px-3 py-2 border-b">Modello</th>
                    <th className="text-left font-medium px-3 py-2 border-b">Tipo macchina</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m, idx) => (
                    <tr
                      key={m.id}
                      className="hover:bg-secondary/40 transition-colors"
                      data-testid={`admin-compat-row-${idx}`}
                    >
                      <td className="px-3 py-2 border-b text-muted-foreground">{idx + 1}</td>
                      <td className="px-3 py-2 border-b font-medium">{m.marca}</td>
                      <td className="px-3 py-2 border-b">
                        <Link
                          to={`/consumabili-per/${m.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[hsl(214,58%,41%)] hover:underline"
                        >
                          {m.nome}
                        </Link>
                      </td>
                      <td className="px-3 py-2 border-b text-muted-foreground">{m.tipoMacchinaLabel || m.tipoMacchina || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Visualizzazione prezzo listino (fonte ERP) ──────────────────────────
function PrezzoListinoEditor({
  sku: _sku,
  initialPrice,
}: {
  sku: string;
  initialPrice: number | null;
  onUpdated?: (newPrice: number) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-secondary/60 last:border-b-0" data-testid="prezzo-listino-editor">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
        Prezzo listino 10
      </span>
      <span className={`text-sm font-mono ${initialPrice != null && initialPrice > 0 ? 'text-foreground font-semibold' : 'text-muted-foreground italic'}`}>
        {initialPrice != null && initialPrice > 0 ? `€ ${initialPrice.toFixed(2).replace('.', ',')}` : '—'}
      </span>
    </div>
  );
}


/** Le tabelle che l'Aggiornamento diretto ha avviato o messo in coda, e che
 *  quindi vale la pena attendere. */
const TABELLE_AVVIATE = ['triggered', 'riavvio', 'in_attesa_turno', 'dopo_completa'];
/** Due tabelle per volta: tutte e quattro finiscono in uno-due minuti. */
const ATTESA_TABELLE_MS = 3 * 60 * 1000;

export default function AdminProductDetailPage() {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const loadProduct = (s: string) => {
    setLoading(true);
    setNotFound(false);
    return productService.get(s)
      .then(setProduct)
      .catch(() => { setProduct(null); setNotFound(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!sku) return;
    loadProduct(sku);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  // La pagina è ancora aperta? L'attesa delle tabelle in background si ferma
  // quando la si lascia.
  const montata = useRef(true);
  useEffect(() => {
    // Anche al rimontaggio (StrictMode in sviluppo smonta e rimonta).
    montata.current = true;
    return () => { montata.current = false; };
  }, []);

  // Aggiornamento diretto dell'articolo (`.scratch/aggiornamenti-erp/spec.md`):
  // anagrafica, listini e sconti subito; testi web, OEM, kit e giacenze in
  // background. Tutti i bottoni "riallinea" della pagina passano da qui.
  // La pagina si ricarica subito, poi di nuovo quando le tabelle in background
  // hanno finito: altrimenti mostra i Codici OEM di prima (osservazioni del
  // 30/10/2026, #17).
  const enrichFromErp = async () => {
    if (!product) return;
    const codice = product.sku;
    setEnriching(true);
    try {
      const r = await aggiornamentiService.direttoArticoli([codice]);
      const m = messaggioDiretto(r, `Articolo ${codice}`);
      await loadProduct(codice);
      const tabelle = Object.entries(r.tabelle ?? {})
        .filter(([, stato]) => TABELLE_AVVIATE.includes(stato))
        .map(([t]) => t);
      if (m.tipo !== 'success' || !r.avviatoAlle || tabelle.length === 0) {
        toast[m.tipo](m.testo, { duration: 6000 });
        return;
      }
      const id = toast.loading(`${m.testo}. Aggiornamento di testi web, Codici OEM, kit e giacenze in corso…`);
      const finite = await attendiTabelle(r.avviatoAlle, tabelle,
        () => productService.get(codice).then(setProduct).catch(() => undefined));
      if (!montata.current) { toast.dismiss(id); return; }
      if (finite) {
        toast.success(`Articolo ${codice}: testi web, Codici OEM, kit e giacenze aggiornati`, { id, duration: 6000 });
      } else {
        toast.info(`Articolo ${codice}: testi web, Codici OEM, kit e giacenze si stanno ancora aggiornando. Ricarica la pagina fra qualche minuto.`, { id, duration: 8000 });
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Il gestionale non ha risposto: riprova fra qualche istante.');
    } finally {
      if (montata.current) setEnriching(false);
    }
  };

  /** Attende che le tabelle in background abbiano finito, chiamando
   *  `ricarica` ogni volta che ne finisce qualcuna (i Codici OEM in ~20 s, le
   *  giacenze dopo un minuto e più). `false` se non finiscono entro
   *  ATTESA_TABELLE_MS o se si lascia la pagina. */
  const attendiTabelle = async (dal: string, tabelle: string[], ricarica: () => Promise<unknown>): Promise<boolean> => {
    const fine = Date.now() + ATTESA_TABELLE_MS;
    let finitePrima = 0;
    while (montata.current && Date.now() < fine) {
      await new Promise(res => setTimeout(res, 3000));
      try {
        const stato = await aggiornamentiService.tabelleArticolo(dal, tabelle);
        const finiteOra = Object.values(stato.tabelle).filter(e => e !== null).length;
        if (finiteOra > finitePrima && montata.current) await ricarica();
        finitePrima = finiteOra;
        if (stato.finite) return true;
      } catch {
        // Un errore di rete non ferma l'attesa: si riprova al giro dopo.
      }
    }
    return false;
  };

  // ── Anagrafica push ERP + OEM editor rimossi (2026-06-03) ─────────
  // Le 2 card "Anagrafica modificabile" e "Codici OEM" erano effettivamente
  // sola consultazione: l'utente ha confermato che i campi NTS non sono mai
  // modificabili dal pannello (l'unico flow è ERP → DB).

  const copySku = () => {
    if (!product) return;
    navigator.clipboard?.writeText(product.sku);
    toast.success('SKU copiato negli appunti');
  };

  // ── States ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-product-detail-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[hsl(214,58%,41%)]" />
          <p className="text-sm text-muted-foreground">Caricamento prodotto…</p>
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-product-detail-not-found">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h1 className="text-xl font-semibold mb-2">Prodotto non trovato</h1>
          <p className="text-sm text-muted-foreground mb-5">
            Lo SKU <span className="font-mono">{sku}</span> non è presente nel catalogo ERP.
          </p>
          <button
            onClick={() => navigate('/admin/prodotti')}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(206,54%,28%)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna al catalogo
          </button>
        </div>
      </div>
    );
  }

  const catLabel = PRODUCT_CATEGORIES.find(c => c.id === product.category)?.title || product.category || '—';
  const dimensions = [product.lunghezza, product.larghezza, product.altezza]
    .filter(d => d != null)
    .map(d => `${d} cm`)
    .join(' × ');

  // Un prodotto è "completo" se ha ricevuto l'arricchimento ERP (job articoli_dettaglio)
  // Riconosciamo lo stato non arricchito se il flag esplicito è false oppure
  // se mancano i campi di base derivati dal dettaglio (brand+category vuoti).
  const isEnriched = product.enriched ?? !!(product.brand && product.category);
  // Fonte di verità per "visibile sul web": valore autoritativo NTS quando
  // presente, fallback su top-level (gestisce overrides demo).
  const effectiveWebVisible = product.webVisibleErp != null ? product.webVisibleErp : product.webVisible;
  const canViewPublic = isEnriched && effectiveWebVisible !== false && !!product.category;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-product-detail-page">
      {/* ── Breadcrumb / back ──────────────────────────────────── */}
      <Link
        to="/admin/prodotti"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="admin-product-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Torna al catalogo
      </Link>

      {/* ── Avviso prodotto non arricchito (banner ambra) ─────── */}
      {!isEnriched && !product.seedOnly && (
        <div
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="admin-product-detail-not-enriched-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <p className="font-semibold text-amber-900">Anagrafica incompleta</p>
            <p className="text-amber-800 text-xs sm:text-sm mt-0.5">
              Mancano marca e categoria: si completano alla prossima esecuzione di Articoli, o subito con
              «Aggiorna dal gestionale».
            </p>
          </div>
          <button
            onClick={enrichFromErp}
            disabled={enriching}
            className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
            data-testid="admin-product-detail-enrich-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${enriching ? 'animate-spin' : ''}`} />
            {enriching ? 'Aggiornamento…' : 'Aggiorna dal gestionale'}
          </button>
        </div>
      )}

      {/* ── Avviso prodotto non visibile sul web ──────────────── */}
      {/* Fonte di verità: erp.detail.webVisibile (autoritativo NTS). Se assente, fallback su top-level. */}
      {(() => {
        const erpVisible = product.webVisibleErp;
        const erpSellable = product.webSellableErp;
        const effectiveVisible = erpVisible != null ? erpVisible : product.webVisible;
        const mismatchVisible = erpVisible != null && erpVisible !== product.webVisible;
        const mismatchSellable = erpSellable != null && erpSellable !== product.webSellable;
        const isDemoOverride = product.webVisibleIsDemo === true;

        if (!isEnriched) return null;

        if (effectiveVisible === false) {
          return (
            <div
              className="mb-4 rounded-xl border border-gray-300 bg-gray-50 p-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-700"
              data-testid="admin-product-detail-not-web-visible-banner"
            >
              <EyeOff className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">
                Questo prodotto è marcato <strong>non visibile sul web</strong> in NTS (<code>ar_webvis=N</code>) — non viene mostrato nel catalogo pubblico ai clienti.
                {isDemoOverride && (
                  <> Tuttavia <strong>il flag locale è ancora <code>webVisibleIsDemo=true</code></strong> (override demo): il portale pubblico potrebbe continuare a mostrarlo finché non riallinei.</>
                )}
              </span>
              {isDemoOverride && (
                <button
                  onClick={enrichFromErp}
                  disabled={enriching}
                  className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg bg-gray-700 hover:bg-gray-800 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                  data-testid="admin-product-detail-realign-demo-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${enriching ? 'animate-spin' : ''}`} />
                  {enriching ? 'Riallineamento…' : 'Riallinea con NTS'}
                </button>
              )}
            </div>
          );
        }

        // Visibile: mostra warning se è solo override demo o se top-level diverge da NTS
        if (isDemoOverride) {
          return (
            <div
              className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-amber-900"
              data-testid="admin-product-detail-demo-visibility-banner"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">
                Visibilità web forzata per la <strong>demo</strong> (flag <code>webVisibleIsDemo=true</code>) — il valore reale NTS è <strong>{erpVisible === false ? 'non visibile' : 'non determinato'}</strong>. Usa <em>Aggiorna dal gestionale</em> per riallineare.
              </span>
              <button
                onClick={enrichFromErp}
                disabled={enriching}
                className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="admin-product-detail-realign-demo-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${enriching ? 'animate-spin' : ''}`} />
                {enriching ? 'Riallineamento…' : 'Riallinea con NTS'}
              </button>
            </div>
          );
        }

        if (mismatchVisible || mismatchSellable) {
          return (
            <div
              className="mb-4 rounded-xl border border-orange-200 bg-orange-50 p-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-orange-900"
              data-testid="admin-product-detail-erp-mismatch-banner"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">
                I flag locali divergono dal gestionale NTS:{' '}
                {mismatchVisible && (
                  <span><strong>visibilità</strong> (NTS: {erpVisible ? 'sì' : 'no'} · locale: {product.webVisible ? 'sì' : 'no'})</span>
                )}
                {mismatchVisible && mismatchSellable && ', '}
                {mismatchSellable && (
                  <span><strong>vendibilità</strong> (NTS: {erpSellable ? 'sì' : 'no'} · locale: {product.webSellable ? 'sì' : 'no'})</span>
                )}
                . Esegui un riallineamento per sincronizzare.
              </span>
              <button
                onClick={enrichFromErp}
                disabled={enriching}
                className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="admin-product-detail-realign-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${enriching ? 'animate-spin' : ''}`} />
                {enriching ? 'Riallineamento…' : 'Riallinea con NTS'}
              </button>
            </div>
          );
        }
        return null;
      })()}

      {/* ── Header card ────────────────────────────────────────── */}
      <div className="rounded-xl border bg-white shadow-sm p-4 sm:p-5 mb-5 flex flex-col sm:flex-row gap-4">
        <img
          src={getProductThumb(product.sku, product.category)}
          alt={product.sku}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border object-cover flex-shrink-0 bg-secondary/30"
          onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[11px] font-bold text-[hsl(214,58%,41%)] tracking-wide font-mono">{product.sku}</span>
            <button
              onClick={copySku}
              className="text-muted-foreground hover:text-foreground"
              title="Copia SKU"
              data-testid="admin-product-detail-copy-sku"
            >
              <Copy className="w-3 h-3" />
            </button>
            {product.seedOnly && (
              <span
                className="text-[9px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded-md"
                title="Prodotto seed (non in NTS)"
                data-testid="admin-product-detail-seed-badge"
              >
                SEED
              </span>
            )}
            {product.rimossoDalGestionale && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-md"
                title="Il gestionale non ha più questo articolo: non si mostra né si vende"
                data-testid="admin-product-detail-rimosso-badge">
                RIMOSSO DAL GESTIONALE
              </span>
            )}
            {product.novita && (
              <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded-md">{product.novita.toUpperCase()}</span>
            )}
          </div>
          <h1 className="text-lg sm:text-xl font-bold tracking-tight leading-snug" data-testid="admin-product-detail-title">
            {product.title || '—'}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
            <span><strong className="text-foreground">{product.brand || '—'}</strong></span>
            <span>•</span>
            <span>{catLabel}</span>
            {product.type && (<><span>•</span><span>{product.type}</span></>)}
            {product.color && product.color !== '-' && (<><span>•</span><span>{product.color}</span></>)}
            {/* Prezzo header */}
            {product.listino10 != null && product.listino10 > 0 ? (
              <>
                <span>•</span>
                <span
                  className="text-base font-bold text-[hsl(214,58%,41%)]"
                  data-testid="admin-product-detail-price"
                >
                  € {product.listino10.toFixed(2).replace('.', ',')}
                </span>
              </>
            ) : (product.webVisible === true) && (
              <>
                <span>•</span>
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded"
                  data-testid="admin-product-detail-no-price-warning"
                  title="Visibile sul web ma manca il prezzo: il portale pubblico lo nasconderà per evitare ordini gratuiti"
                >
                  <AlertTriangle className="w-3 h-3" /> Manca prezzo
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          {canViewPublic ? (
            <Link
              to={`/prodotti/${product.category}/${product.sku}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium hover:bg-secondary/60 transition-colors whitespace-nowrap"
              data-testid="admin-product-detail-view-public"
            >
              <Edit3 className="w-3.5 h-3.5" /> Vista pubblica
            </Link>
          ) : (
            <span
              className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border bg-gray-50 text-xs font-medium text-muted-foreground cursor-not-allowed whitespace-nowrap"
              title={!isEnriched
                ? "Vista pubblica disponibile dopo l'arricchimento ERP"
                : "Prodotto non visibile sul web (flag NTS)"}
              data-testid="admin-product-detail-view-public-disabled"
            >
              <EyeOff className="w-3.5 h-3.5" /> Vista pubblica n/d
            </span>
          )}
          <button
            onClick={enrichFromErp}
            disabled={enriching}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
            data-testid="admin-product-detail-refresh-erp"
            title="Rilegge subito l'articolo dal gestionale"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${enriching ? 'animate-spin' : ''}`} />
            {enriching ? 'Aggiornamento…' : 'Aggiorna dal gestionale'}
          </button>
        </div>
      </div>

      {/* ── Layout grid ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Anagrafica */}
        <Section icon={Tag} title="Anagrafica & Categoria">
          <Field label="Codice DTK / SKU" value={product.sku} mono />
          <Field label="Codici OEM" value={(product.oemCodes || []).join(', ')} mono />
          <PrezzoListinoEditor
            sku={product.sku}
            initialPrice={product.listino10 ?? null}
          />
          <Field
            label="Marca"
            value={product.erp?.detail?.brand?.label || product.brand}
          />
          <Field
            label="Gruppo merceologico"
            value={product.erp?.detail?.gruppo?.label || catLabel}
          />
          <Field
            label="Sottogruppo merceologico"
            value={product.erp?.detail?.sottogruppo?.label || product.categoriaMerceologica}
          />
          <Field
            label="Famiglia"
            value={product.erp?.detail?.famigliaProdotto?.label || product.famiglia}
          />
          <Field label="Tipo" value={product.type} />
          <Field
            label="Colore"
            value={
              product.erp?.detail?.colore
                ? `${product.erp.detail.colore.code ?? ''}${product.erp.detail.colore.label ? ' - ' + product.erp.detail.colore.label : ''}`
                : (product.color || product.coloreDescrizione)
            }
          />
          <Field label="Originale" value={product.originale === true ? 'Sì' : product.originale === false ? 'No' : undefined} />
          <Field label="È un Kit" value={product.isKit === true ? 'Sì' : product.isKit === false ? 'No' : undefined} />
        </Section>

        {/* Stock & logistica */}
        <Section icon={Boxes} title="Stock & Logistica">
          <Field
            label="Pezzi in magazzino"
            value={
              typeof product.stock === 'number'
                ? <span className={product.stock > 0 ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'} data-testid="admin-product-stock">{product.stock.toLocaleString('it')} pz</span>
                : <span className="text-muted-foreground" data-testid="admin-product-stock">—</span>
            }
          />
          <Field label="Unità di misura" value={product.unitaDiMisura} />
          <Field label="Minimo vendibile" value={product.minimoVendibile} />
          <Field label="Confezione (pz)" value={product.confezione} />
          <Field label="Richiede abbinamento" value={product.richiedeAbbinamento === true ? 'Sì' : product.richiedeAbbinamento === false ? 'No' : undefined} />
          <Field label="In esaurimento" value={product.inEsaurimento === true ? 'Sì' : product.inEsaurimento === false ? 'No' : undefined} />
          <Field label="Durata" value={product.yield} />
          <Field label="Classe sconto" value={product.classeSconto} mono />
        </Section>

        {/* Dimensioni & Peso */}
        <Section icon={Ruler} title="Dimensioni & Peso">
          <Field label="Lung. × Larg. × Alt." value={dimensions || undefined} />
          <Field label="Volume (m³)" value={product.volume} />
          <Field label="Peso lordo (kg)" value={product.pesoLordo} />
          <Field label="Peso netto (kg)" value={product.pesoNetto} />
        </Section>

        {/* Identificativi ERP — sezione nascosta (2026-06-03):
            gli ID interni NTS (idMarca/idFamiglia/idCategoriaMerc./idColore/idUdm
            /idTipologiaNovita) non hanno valore operativo per il backoffice DTK.
            Le label umane sono già nella card "Anagrafica & Categoria".
            Mantenuti i campi nello schema per uso futuro / debug. */}

        {/* Date */}
        <Section icon={Calendar} title="Date e novità">
          <Field label="Data inserimento" value={product.dataInserimento} />
          <Field label="Ultima modifica" value={product.dataUltimaModifica} />
          <Field label="Novità" value={product.novita} />
          <Field label="Scadenza novità" value={product.dataScadenzaNovita} />
        </Section>

        {/* Compatibilità (full width) — fonte: abbinatore_associazioni */}
        <div className="lg:col-span-2 rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="admin-product-compat-section">
          <CompatibilitaCard sku={product.sku} />
        </div>

        {/* Descrizione estesa (full width) */}
        <div className="lg:col-span-2 rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <FileText className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Descrizione estesa</h2>
          </div>
          <div className="p-4">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {product.descrizione || <span className="text-muted-foreground italic">Nessuna descrizione presente.</span>}
            </p>
          </div>
        </div>

        {/* Editor anagrafica e Codici OEM rimossi (2026-06-03) —
            entrambi i pannelli erano sostanzialmente read-only:
            - "Anagrafica modificabile": i campi (peso, dimensioni, web flags)
              sono dovuti restare come consultazione, NON push verso NTS.
            - "Codici OEM": il sync NTS è source of truth; la UI non aggiungeva
              valore. Per consultarli usa la card Identificativi sopra. */}

        {/* Listini & override prezzi cliente — nascosto su richiesta (2026-05-14).
            La sezione mostrava la tabella `Listini` con tutti gli override per cliente
            e l'editor 1-record. Il PrezzoListinoEditor inline gestisce ora il caso
            comune (prezzo listino 10 generico, conto=0) e basta per il backoffice DTK. */}
        {false && product && (
          <div className="lg:col-span-2">
            <ListiniBycodart codart={product.sku} />
          </div>
        )}

        {/* Immagini articolo — upload/rimozione/primaria, compressione automatica */}
        {product && (
          <div className="lg:col-span-2">
            <AdminProductImages
              sku={product.sku}
              images={product.images || []}
              onChange={(images) => setProduct(p => (p ? { ...p, images } : p))}
            />
          </div>
        )}

        {/* Sconto scatola — gestione DTK locale (no ERP) */}
        {product && (
          <div className="lg:col-span-2">
            <BoxDiscountEditor product={product} />
          </div>
        )}

        {/* Componenti Kit (readonly, fonte ERP HhArtkitApri) */}
        {product?.isKit && (
          <div className="lg:col-span-2 rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="admin-product-kit-section">
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
              <Boxes className="w-4 h-4 text-[hsl(214,58%,41%)]" />
              <h2 className="text-sm font-semibold tracking-tight">Composizione Kit</h2>
              {product.tipoKit ? (
                <span
                  className={`ml-1 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${
                    product.tipoKit === 'S'
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-purple-300 bg-purple-50 text-purple-700'
                  }`}
                  title={product.tipoKit === 'S' ? 'Tipo S: prezzo = somma dei figli' : 'Tipo A: prezzo del padre, figli a 0'}
                  data-testid="admin-kit-type-badge"
                >
                  Tipo {product.tipoKit}
                </span>
              ) : null}
            </div>
            <div className="p-4">
              {product.kitItems && product.kitItems.length > 0 ? (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    {product.kitItems.length} componente{product.kitItems.length === 1 ? '' : 'i'} —
                    {' '}
                    {product.tipoKit === 'S'
                      ? <span>il prezzo del kit è la <strong>somma dei prezzi figli</strong> moltiplicati per la quantità.</span>
                      : product.tipoKit === 'A'
                      ? <span>prezzo del kit definito sul <strong>padre</strong>; i figli sono inclusi nel pacchetto a costo 0.</span>
                      : <span>tipologia prezzo kit non definita lato ERP.</span>}
                  </p>
                  <div className="overflow-x-auto -mx-4 sm:mx-0" data-testid="admin-kit-items-table">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-[hsl(var(--dtk-canvas))] text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="text-left font-medium px-3 py-2 border-b w-12">#</th>
                          <th className="text-left font-medium px-3 py-2 border-b">Codice figlio</th>
                          <th className="text-left font-medium px-3 py-2 border-b">Descrizione</th>
                          <th className="text-right font-medium px-3 py-2 border-b">Quantità</th>
                          <th className="text-left font-medium px-3 py-2 border-b">UM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.kitItems.map((it, idx) => (
                          <tr key={`${it.sku}-${idx}`} className="hover:bg-secondary/40 transition-colors" data-testid={`admin-kit-item-row-${idx}`}>
                            <td className="px-3 py-2 border-b text-muted-foreground">{idx + 1}</td>
                            <td className="px-3 py-2 border-b font-mono">
                              <Link
                                to={`/admin/prodotti/${encodeURIComponent(it.sku)}`}
                                className="text-[hsl(214,58%,41%)] hover:underline"
                              >
                                {it.sku}
                              </Link>
                            </td>
                            <td className="px-3 py-2 border-b">{it.label || <span className="text-muted-foreground italic">—</span>}</td>
                            <td className="px-3 py-2 border-b text-right tabular-nums font-medium">{it.qty}</td>
                            <td className="px-3 py-2 border-b text-muted-foreground">{it.um || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : product.tipoKit === 'A' ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Kit di <strong>tipo A</strong> senza componenti sincronizzati da <code className="font-mono">HhArtkitApri</code>.
                  Lato ERP il prezzo è gestito sul padre; i figli sono inclusi nel pacchetto a costo 0.
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">Nessun componente kit sincronizzato dal gestionale.</p>
              )}
            </div>
          </div>
        )}

        {/* Offerta a tempo — override Mongo (no ERP) */}
        {product && (
          <div className="lg:col-span-2">
            <PromoEditor product={product} />
          </div>
        )}

        <div className="lg:col-span-2">
          <AdminProductAttachments
            sku={product.sku}
            attachments={product.attachments || []}
            onChange={(attachments) => setProduct(p => (p ? { ...p, attachments } : p))}
          />
        </div>
      </div>
    </div>
  );
}

// AnagField rimosso (2026-06-03): la sezione "Anagrafica modificabile"
// è stata eliminata, non c'è più alcun consumer.


